import $ from 'legacy/jquery';
import { isRunningInsideiOSWebView } from './runner_detector';
import UrlHelper from './url_helper';
import { IllegalStateError } from '../errors';
import { isValidInternationalNumber } from '../../shared/utils/phoneUtil';

/* global WebKitMutationObserver: false */
/* eslint no-empty: 0 */

/**
 * THIS IS NOT TO BE INITIALIZED DIRECTLY (except for testing)
 *
 * It should be instantiated and initialized indirectly via WebViewHelper
 *
 * iOS APIs allow apps to expose special javascript handlers under
 * `window.webkit.messageHandlers.<handler-name>`.
 *
 * This allows webapps to send the app arbitrary data via
 * `window.webkit.messageHandlers.<handler-name>.postMessage` function
 * which can be used to handle multiple use cases seamlessly.
 *
 * @param {UrlHelper} urlHelper - a UrlHelper class instance to use for URL checks
 *
 * @see WebViewHelper
 * @see UrlHelper
 * @see Android
 * */
export default function iOS(urlHelper) {
  let initialized = false,
    domMutationObserver;

  let self = this;

  /**
   * Just an indentifier for the app to tell what has been
   * initialized
   * */
  self.type = 'iOS';

  /**
   * Clones the specified element and copies over
   * all the attributes to the new element of the specified
   * type
   *
   * For simplicity, this doesn't copy over bound
   * event handlers on the original element.
   *
   * @param {jQuery} $elem - the DOM element to convert
   * @param {string} newElemType - the string specifying the element type (e.g span)
   *
   * For logic on why this function exists:
   *
   * @see deactivateInvalidPhoneLinks
   * */
  function convertElemTo($elem, newElemType) {
    const $newElem = $('<' + newElemType + '></' + newElemType + '>');

    // copy the attributes from old element to new
    // element
    for (
      let i = 0, attrs = $elem[0].attributes, len = attrs.length;
      i < len;
      i++
    ) {
      $newElem.attr(attrs[i].nodeName, attrs[i].nodeValue);
    }

    // copy the content
    $newElem.html($elem.html());

    // attach a special attribute to track converted
    // elements
    const origElemAttr = 'data-original-elem';
    if ($newElem.attr(origElemAttr) === newElemType) {
      $newElem.removeAttr(origElemAttr);
    } else {
      $newElem.attr(origElemAttr, $elem[0].nodeName.toLowerCase());
    }

    $newElem.insertBefore($elem);
    $elem.remove();
  }

  /**
   * If an invalid phone link is found,
   * the <a> will be replaced with an <span>
   * instead
   *
   * This is needed by iOS because the dialer errors out
   * on invalid phone numbers that are clicked by the user.
   *
   * There can be invalid phone numbers shown to the user because
   * there is no proper validation of this data when it is entered in
   * the desktop candidate details tab
   * */
  function deactivateInvalidPhoneLinks($elem) {
    const href = $elem.attr('href');

    if (!href || !urlHelper.isPhoneLink(href)) {
      return;
    }

    // the libphonenumber throws an error if
    // the href contains no numbers like `tel:sdsfssdf`
    // instead of something like `tel:3334445555`
    try {
      if (isValidInternationalNumber(href)) {
        return true;
      }
    } catch (e) {}

    convertElemTo($elem, 'span');
  }

  function onDomMutations(mutations) {
    mutations.forEach(function (mutation) {
      // if an attribute changed (i.e href) or a new element was added, find any new links and de-activate them
      if (
        mutation.type === 'attributes' ||
        (mutation.type === 'childList' && mutation.addedNodes.length > 0)
      ) {
        $(mutation.target)
          .find('a')
          .each(function () {
            deactivateInvalidPhoneLinks($(this));
          });
      }
    });
  }

  function watchForDomChanges() {
    const Observer = MutationObserver || WebKitMutationObserver;

    domMutationObserver = new Observer(onDomMutations);

    domMutationObserver.observe(document, {
      childList: true, // watch for add/removal of child elements
      subtree: true, // watch for changes in descendants
      attributes: true, // watch for changes in attributes
      attributeFilter: ['href'], // only watch for attribute changes in 'href'
    });
  }

  /**
   * Event handler for when a user clicks on a link. It automatically
   * determines what type of handler it needs to send to and calls
   * the relevant functions
   * */
  function onLinkClick(evt) {
    const $elem = $(evt.currentTarget);
    const href = $elem.attr('href');

    // make sure no click behavior is fired no matter
    // if they are allowlisted or not
    evt.preventDefault();

    if (
      urlHelper.isPlaceholderLink(href) ||
      !urlHelper.isAllowlistedLink($elem)
    ) {
      return;
    }

    const info = urlHelper.getNormalizedDataFromLink($elem);
    const handler = info.handler;

    // the handler name isn't needed by iOS so
    // delete it
    delete info.handler;

    self.postMessage(handler, info);
  }

  self.init = function () {
    if (!urlHelper || !(urlHelper instanceof UrlHelper)) {
      throw new TypeError('UrlHelper object param is not of the correct type');
    }

    if (!isRunningInsideiOSWebView()) {
      throw new IllegalStateError('iOS interface is not exposed!');
    }

    if (initialized) {
      return;
    }

    // make sure to not take any chances and disable all invalid phone links
    $('a').each(function () {
      deactivateInvalidPhoneLinks($(this));
    });

    // if there are DOM changes, rerun the search and deactivate any invalid phone links
    watchForDomChanges();

    $(document).on('click', 'a', onLinkClick);

    initialized = true;
  };

  self.isInitialized = function () {
    return initialized;
  };

  self.haveDeviceNavigateTo = function (url, title, isForNativeScreen) {
    const info = urlHelper.getNormalizedData(
      url,
      urlHelper.handlers.NAVIGATION,
      title,
      isForNativeScreen
    );

    const handler = info.handler;

    // the handler name isn't needed by iOS so
    // delete it
    delete info.handler;

    self.postMessage(handler, info);
  };

  self.haveDeviceDeeplinkTo = function (url, title, isForNativeScreen) {
    const info = urlHelper.getNormalizedData(
      url,
      urlHelper.handlers.DEEPLINK,
      title,
      isForNativeScreen
    );

    const handler = info.handler;

    // the handler name isn't needed by iOS so
    // delete it
    delete info.handler;

    self.postMessage(handler, info);
  };

  self.reportProgress = function (percentLoaded) {
    self.postMessage(urlHelper.handlers.METADATA, {
      progress: percentLoaded,
    });
  };

  /**
   * iOS webviews have special hooks not in regular browsers that
   * use a variant of postMessage
   * To send a message, you do:
   *    window.webkit.messageHandlers.<handler-name>.postMessage(obj);
   *
   * This just piggybacks off of that with a few checks.
   *
   * If the JS isn't being run from within a webview, then this
   * operation is a NOOP
   *
   * For handling user clicks, @see onLinkClick
   * */
  self.postMessage = function (handler, obj) {
    if (
      !isRunningInsideiOSWebView() ||
      !window.webkit.messageHandlers[handler]
    ) {
      return;
    }

    window.webkit.messageHandlers[handler].postMessage(obj);
  };

  self.destroy = function () {
    if (initialized) {
      $(document).off('click', 'a', onLinkClick);

      $('[data-original-elem]').each(function () {
        convertElemTo($(this), $(this).attr('data-original-elem'));
      });

      initialized = false;
      domMutationObserver.disconnect();
    }
  };
}
